import { useState, useEffect, useCallback } from "react";
import { useConfig } from "./ConfiguracoesLayout";
import ConfigSection from "./ConfigSection";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  MessageSquare, Wifi, WifiOff, QrCode, Loader2, Send,
  RefreshCw, Trash2, Plus, Check, X, Phone, Bell, Megaphone,
  Bot, UserRound, Sparkles, GitBranch, GripVertical
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface WhatsAppInstance {
  id: string;
  restaurant_id: string;
  instance_name: string;
  status: string;
  phone_number: string | null;
  qr_code: string | null;
  auto_notify_new_order: boolean;
  auto_notify_status_change: boolean;
  auto_notify_delivery: boolean;
}

interface WhatsAppTemplate {
  id: string;
  name: string;
  trigger_event: string;
  content: string;
  is_active: boolean;
}

interface WhatsAppMessage {
  id: string;
  direction: string;
  remote_phone: string;
  content: string;
  status: string;
  created_at: string;
  message_type: string;
}

interface ChatbotConfig {
  id: string;
  restaurant_id: string;
  is_enabled: boolean;
  is_human_takeover: boolean;
  system_prompt: string;
  greeting_message: string;
  fallback_message: string;
  max_context_messages: number;
  menu_url: string;
}

interface ChatbotConversation {
  id: string;
  remote_phone: string;
  status: string;
  last_message_at: string;
  human_takeover_at: string | null;
}

interface ChatbotFlow {
  id: string;
  restaurant_id: string;
  trigger_keywords: string[];
  title: string;
  response_text: string;
  buttons: { label: string; value: string }[];
  sort_order: number;
  is_active: boolean;
}

const defaultTemplates = [
  { name: "Novo Pedido", trigger_event: "new_order", content: "🛒 *Novo Pedido Recebido!*\n\nOlá {{customer_name}}! Seu pedido #{{order_id}} foi recebido.\n\n💰 Total: R$ {{total}}\n📍 {{order_type}}" },
  { name: "Em Preparo", trigger_event: "preparing", content: "👨‍🍳 *Pedido em Preparo*\n\nOlá {{customer_name}}! Seu pedido #{{order_id}} já está sendo preparado. 🔥" },
  { name: "Pronto", trigger_event: "ready", content: "✅ *Pedido Pronto!*\n\nOlá {{customer_name}}! Seu pedido #{{order_id}} está pronto.\n\n{{ready_message}}" },
  { name: "Saiu para Entrega", trigger_event: "delivering", content: "🛵 *Saiu para Entrega!*\n\nOlá {{customer_name}}! Seu pedido #{{order_id}} saiu para entrega. 📦" },
  { name: "Concluído", trigger_event: "completed", content: "🎉 *Pedido Entregue!*\n\nOlá {{customer_name}}! Obrigado pela preferência! ⭐" },
  { name: "Cancelado", trigger_event: "cancelled", content: "❌ *Pedido Cancelado*\n\nOlá {{customer_name}}, seu pedido #{{order_id}} foi cancelado." },
];

const WhatsAppConfig = () => {
  const { restaurant, toast: configToast } = useConfig();
  const { toast } = useToast();
  const [instance, setInstance] = useState<WhatsAppInstance | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [refreshingQr, setRefreshingQr] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("Olá! Esta é uma mensagem de teste do ViaMenu 🚀");
  const [sendingTest, setSendingTest] = useState(false);
  const [chatbotConfig, setChatbotConfig] = useState<ChatbotConfig | null>(null);
  const [chatbotConversations, setChatbotConversations] = useState<ChatbotConversation[]>([]);
  const [savingChatbot, setSavingChatbot] = useState(false);
  const [chatbotFlows, setChatbotFlows] = useState<ChatbotFlow[]>([]);
  const [savingFlows, setSavingFlows] = useState(false);

  const loadData = useCallback(async () => {
    if (!restaurant) return;
    setLoading(true);

    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;
    if (!token) return;

    // Load instance status
    const res = await supabase.functions.invoke('whatsapp-instance', {
      body: { action: 'status', restaurant_id: restaurant.id },
    });
    if (res.data?.instance) setInstance(res.data.instance);

    // Load templates
    const { data: tpls } = await supabase
      .from('whatsapp_templates')
      .select('*')
      .eq('restaurant_id', restaurant.id)
      .order('created_at');
    setTemplates((tpls as any) || []);

    // Load recent messages
    const { data: msgs } = await supabase
      .from('whatsapp_messages')
      .select('*')
      .eq('restaurant_id', restaurant.id)
      .order('created_at', { ascending: false })
      .limit(50);
    setMessages((msgs as any) || []);

    // Load chatbot config
    const { data: cbConfig } = await supabase
      .from('whatsapp_chatbot_config')
      .select('*')
      .eq('restaurant_id', restaurant.id)
      .maybeSingle();
    setChatbotConfig(cbConfig as any);

    // Load chatbot conversations
    const { data: convs } = await supabase
      .from('whatsapp_chatbot_conversations')
      .select('*')
      .eq('restaurant_id', restaurant.id)
      .order('last_message_at', { ascending: false })
      .limit(20);
    setChatbotConversations((convs as any) || []);

    // Load chatbot flows
    const { data: flowsData } = await supabase
      .from('whatsapp_chatbot_flows')
      .select('*')
      .eq('restaurant_id', restaurant.id)
      .order('sort_order', { ascending: true });
    setChatbotFlows((flowsData as any) || []);

    setLoading(false);
  }, [restaurant]);

  useEffect(() => { loadData(); }, [loadData]);

  // Realtime for instance status
  useEffect(() => {
    if (!restaurant) return;
    const channel = supabase
      .channel('whatsapp-instance-updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'whatsapp_instances',
        filter: `restaurant_id=eq.${restaurant.id}`,
      }, (payload: any) => {
        if (payload.new) setInstance(payload.new as WhatsAppInstance);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [restaurant]);

  const handleCreate = async () => {
    if (!restaurant) return;
    setCreating(true);
    try {
      const res = await supabase.functions.invoke('whatsapp-instance', {
        body: { action: 'create', restaurant_id: restaurant.id },
      });
      if (res.error) throw new Error(res.error.message);
      if (res.data?.instance) setInstance(res.data.instance);
      toast({ title: "Instância criada!", description: "Escaneie o QR Code com seu WhatsApp." });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const handleRefreshQr = async () => {
    if (!restaurant) return;
    setRefreshingQr(true);
    try {
      const res = await supabase.functions.invoke('whatsapp-instance', {
        body: { action: 'qrcode', restaurant_id: restaurant.id },
      });
      if (res.data?.qr_code) {
        setInstance(prev => prev ? { ...prev, qr_code: res.data.qr_code, status: 'connecting' } : prev);
      }
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setRefreshingQr(false);
    }
  };

  const handleDisconnect = async () => {
    if (!restaurant) return;
    await supabase.functions.invoke('whatsapp-instance', {
      body: { action: 'disconnect', restaurant_id: restaurant.id },
    });
    setInstance(prev => prev ? { ...prev, status: 'disconnected', qr_code: null, phone_number: null } : prev);
    toast({ title: "WhatsApp desconectado" });
  };

  const handleDelete = async () => {
    if (!restaurant) return;
    await supabase.functions.invoke('whatsapp-instance', {
      body: { action: 'delete', restaurant_id: restaurant.id },
    });
    setInstance(null);
    toast({ title: "Instância removida" });
  };

  const handleUpdateNotification = async (field: string, value: boolean) => {
    if (!instance) return;
    await supabase.from('whatsapp_instances')
      .update({ [field]: value } as any)
      .eq('id', instance.id);
    setInstance(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const handleSaveTemplates = async () => {
    if (!restaurant) return;
    for (const tpl of templates) {
      await supabase.from('whatsapp_templates')
        .upsert({ ...tpl, restaurant_id: restaurant.id } as any, { onConflict: 'id' });
    }
    toast({ title: "Templates salvos!" });
  };

  const handleInitDefaultTemplates = async () => {
    if (!restaurant) return;
    const inserts = defaultTemplates.map(t => ({
      restaurant_id: restaurant.id,
      name: t.name,
      trigger_event: t.trigger_event,
      content: t.content,
      is_active: true,
    }));
    const { data } = await supabase.from('whatsapp_templates')
      .insert(inserts as any)
      .select();
    if (data) setTemplates(data as any);
    toast({ title: "Templates padrão criados!" });
  };

  const handleSendTest = async () => {
    if (!restaurant || !testPhone) return;
    setSendingTest(true);
    try {
      const res = await supabase.functions.invoke('whatsapp-send', {
        body: { restaurant_id: restaurant.id, phone: testPhone, message: testMessage },
      });
      if (res.data?.success) {
        toast({ title: "Mensagem enviada!", description: `Para ${testPhone}` });
      } else {
        toast({ title: "Erro ao enviar", description: res.data?.error || "Erro desconhecido", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSendingTest(false);
    }
  };
  const handleSaveChatbot = async () => {
    if (!restaurant) return;
    setSavingChatbot(true);
    try {
      if (chatbotConfig?.id) {
        await supabase.from('whatsapp_chatbot_config')
          .update({
            is_enabled: chatbotConfig.is_enabled,
            system_prompt: chatbotConfig.system_prompt,
            greeting_message: chatbotConfig.greeting_message,
            fallback_message: chatbotConfig.fallback_message,
            max_context_messages: chatbotConfig.max_context_messages,
            menu_url: chatbotConfig.menu_url || null,
          } as any)
          .eq('id', chatbotConfig.id);
      } else {
        const { data } = await supabase.from('whatsapp_chatbot_config')
          .insert({
            restaurant_id: restaurant.id,
            is_enabled: true,
            system_prompt: chatbotConfig?.system_prompt || 'Você é um assistente virtual de atendimento do restaurante. Seja educado, prestativo e objetivo.',
            greeting_message: chatbotConfig?.greeting_message || 'Olá! 👋 Sou o assistente virtual. Como posso te ajudar?',
            fallback_message: chatbotConfig?.fallback_message || 'Desculpe, não consegui entender. Vou encaminhar para um atendente humano. 🙏',
          } as any)
          .select()
          .single();
        if (data) setChatbotConfig(data as any);
      }
      toast({ title: "Chatbot salvo!" });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSavingChatbot(false);
    }
  };

  const handleToggleChatbot = async (enabled: boolean) => {
    if (!restaurant) return;
    if (!chatbotConfig) {
      // Create config
      const { data } = await supabase.from('whatsapp_chatbot_config')
        .insert({
          restaurant_id: restaurant.id,
          is_enabled: enabled,
        } as any)
        .select()
        .single();
      if (data) setChatbotConfig(data as any);
    } else {
      await supabase.from('whatsapp_chatbot_config')
        .update({ is_enabled: enabled } as any)
        .eq('id', chatbotConfig.id);
      setChatbotConfig({ ...chatbotConfig, is_enabled: enabled });
    }
    toast({ title: enabled ? "Chatbot IA ativado!" : "Chatbot IA desativado" });
  };

  const handleResetConversation = async (convId: string) => {
    await supabase.from('whatsapp_chatbot_conversations')
      .update({ status: 'bot_active', human_takeover_at: null } as any)
      .eq('id', convId);
    setChatbotConversations(prev => prev.map(c => c.id === convId ? { ...c, status: 'bot_active', human_takeover_at: null } : c));
    toast({ title: "Conversa devolvida ao bot" });
  };

  // ─── Flows handlers ───
  const handleAddFlow = () => {
    const newFlow: ChatbotFlow = {
      id: `new_${Date.now()}`,
      restaurant_id: restaurant?.id || '',
      trigger_keywords: [],
      title: '',
      response_text: '',
      buttons: [],
      sort_order: chatbotFlows.length,
      is_active: true,
    };
    setChatbotFlows(prev => [...prev, newFlow]);
  };

  const handleUpdateFlow = (index: number, field: keyof ChatbotFlow, value: any) => {
    setChatbotFlows(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  };

  const handleRemoveFlow = async (index: number) => {
    const flow = chatbotFlows[index];
    if (flow.id && !flow.id.startsWith('new_')) {
      await supabase.from('whatsapp_chatbot_flows').delete().eq('id', flow.id);
    }
    setChatbotFlows(prev => prev.filter((_, i) => i !== index));
    toast({ title: "Fluxo removido" });
  };

  const handleAddFlowButton = (flowIndex: number) => {
    const flow = chatbotFlows[flowIndex];
    if (flow.buttons.length >= 3) return;
    handleUpdateFlow(flowIndex, 'buttons', [...flow.buttons, { label: '', value: '' }]);
  };

  const handleUpdateFlowButton = (flowIndex: number, btnIndex: number, field: string, value: string) => {
    const flow = chatbotFlows[flowIndex];
    const newButtons = flow.buttons.map((b, i) => i === btnIndex ? { ...b, [field]: value } : b);
    handleUpdateFlow(flowIndex, 'buttons', newButtons);
  };

  const handleRemoveFlowButton = (flowIndex: number, btnIndex: number) => {
    const flow = chatbotFlows[flowIndex];
    handleUpdateFlow(flowIndex, 'buttons', flow.buttons.filter((_, i) => i !== btnIndex));
  };

  const handleSaveFlows = async () => {
    if (!restaurant) return;
    setSavingFlows(true);
    try {
      for (let i = 0; i < chatbotFlows.length; i++) {
        const flow = chatbotFlows[i];
        const payload = {
          restaurant_id: restaurant.id,
          trigger_keywords: flow.trigger_keywords,
          title: flow.title,
          response_text: flow.response_text,
          buttons: flow.buttons,
          sort_order: i,
          is_active: flow.is_active,
        };

        if (flow.id.startsWith('new_')) {
          const { data } = await supabase.from('whatsapp_chatbot_flows')
            .insert(payload as any).select().single();
          if (data) {
            setChatbotFlows(prev => prev.map((f, idx) => idx === i ? { ...f, id: (data as any).id } : f));
          }
        } else {
          await supabase.from('whatsapp_chatbot_flows')
            .update(payload as any).eq('id', flow.id);
        }
      }
      toast({ title: "Fluxos salvos com sucesso!" });
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSavingFlows(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-green-500" />
          WhatsApp Integration
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Conecte seu WhatsApp para enviar notificações automáticas de pedidos
        </p>
      </div>

      <Tabs defaultValue="connection" className="space-y-4">
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="connection" className="text-xs">Conexão</TabsTrigger>
          <TabsTrigger value="chatbot" className="text-xs gap-1"><Bot className="h-3 w-3" /> Chatbot</TabsTrigger>
          <TabsTrigger value="flows" className="text-xs gap-1"><GitBranch className="h-3 w-3" /> Fluxos</TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs">Notificações</TabsTrigger>
          <TabsTrigger value="templates" className="text-xs">Templates</TabsTrigger>
          <TabsTrigger value="messages" className="text-xs">Mensagens</TabsTrigger>
        </TabsList>

        {/* Connection Tab */}
        <TabsContent value="connection" className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700/40">
            <Phone className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Use um número comercial</p>
              <p className="text-xs text-amber-700/80 dark:text-amber-400/70 mt-0.5">
                Recomendamos conectar um número de WhatsApp exclusivo para o restaurante. Evite usar seu número pessoal, pois todas as notificações e mensagens serão enviadas por esse número.
              </p>
            </div>
          </div>
          {!instance ? (
            <Card>
              <CardContent className="pt-6 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                  <MessageSquare className="h-8 w-8 text-green-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Conectar WhatsApp</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Crie uma instância para conectar seu número de WhatsApp
                  </p>
                </div>
                <Button onClick={handleCreate} disabled={creating} className="bg-green-600 hover:bg-green-700 gap-2">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Criar Instância
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Status Card */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Status da Conexão</CardTitle>
                    <Badge variant={instance.status === 'connected' ? 'default' : 'secondary'}
                      className={instance.status === 'connected' ? 'bg-green-600' : ''}>
                      {instance.status === 'connected' && <Wifi className="h-3 w-3 mr-1" />}
                      {instance.status === 'disconnected' && <WifiOff className="h-3 w-3 mr-1" />}
                      {instance.status === 'connecting' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                      {instance.status === 'connected' ? 'Conectado' : 
                       instance.status === 'connecting' ? 'Conectando...' : 'Desconectado'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Instância:</span>
                      <p className="font-mono text-xs mt-0.5">{instance.instance_name}</p>
                    </div>
                    {instance.phone_number && (
                      <div>
                        <span className="text-muted-foreground">Número:</span>
                        <p className="font-medium mt-0.5 flex items-center gap-1">
                          <Phone className="h-3 w-3" /> +{instance.phone_number}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {instance.status === 'connected' ? (
                      <Button variant="outline" size="sm" onClick={handleDisconnect} className="text-red-500 border-red-200">
                        <WifiOff className="h-3.5 w-3.5 mr-1" /> Desconectar
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={handleRefreshQr} disabled={refreshingQr}>
                        {refreshingQr ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <QrCode className="h-3.5 w-3.5 mr-1" />}
                        Gerar QR Code
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={loadData}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Atualizar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-500">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remover
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* QR Code */}
              {instance.status !== 'connected' && instance.qr_code && (
                <Card>
                  <CardContent className="pt-6 text-center space-y-3">
                    <p className="text-sm font-medium">Escaneie o QR Code com seu WhatsApp</p>
                    <div className="inline-block p-3 bg-white rounded-xl shadow-md">
                      <img src={instance.qr_code} alt="QR Code WhatsApp" className="w-64 h-64" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Abra o WhatsApp → Menu (⋮) → Dispositivos conectados → Conectar dispositivo
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Test Send */}
              {instance.status === 'connected' && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Enviar Mensagem de Teste</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs">Número (com DDD)</Label>
                      <Input placeholder="11999998888" value={testPhone} onChange={e => setTestPhone(e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Mensagem</Label>
                      <Textarea value={testMessage} onChange={e => setTestMessage(e.target.value)} rows={3} />
                    </div>
                    <Button onClick={handleSendTest} disabled={sendingTest || !testPhone} className="bg-green-600 hover:bg-green-700 gap-2">
                      {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Enviar Teste
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="h-4 w-4" /> Notificações Automáticas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'auto_notify_new_order', label: 'Novo pedido recebido', desc: 'Enviar mensagem quando um novo pedido é criado' },
                { key: 'auto_notify_status_change', label: 'Mudança de status', desc: 'Notificar quando o status do pedido muda (preparo, pronto, concluído)' },
                { key: 'auto_notify_delivery', label: 'Saiu para entrega', desc: 'Avisar o cliente quando o pedido sai para entrega' },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                  <Switch
                    checked={(instance as any)?.[item.key] ?? true}
                    onCheckedChange={(v) => handleUpdateNotification(item.key, v)}
                    disabled={!instance || instance.status !== 'connected'}
                  />
                </div>
              ))}

              {(!instance || instance.status !== 'connected') && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  Conecte o WhatsApp primeiro para habilitar as notificações
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          {templates.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center space-y-3">
                <p className="text-sm text-muted-foreground">Nenhum template criado ainda</p>
                <Button onClick={handleInitDefaultTemplates} variant="outline" className="gap-2">
                  <Plus className="h-4 w-4" /> Criar Templates Padrão
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {templates.map((tpl, i) => (
                <Card key={tpl.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">{tpl.name}</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{tpl.trigger_event}</Badge>
                        <Switch
                          checked={tpl.is_active}
                          onCheckedChange={(v) => {
                            const updated = [...templates];
                            updated[i] = { ...updated[i], is_active: v };
                            setTemplates(updated);
                          }}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={tpl.content}
                      onChange={(e) => {
                        const updated = [...templates];
                        updated[i] = { ...updated[i], content: e.target.value };
                        setTemplates(updated);
                      }}
                      rows={4}
                      className="font-mono text-xs"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Variáveis disponíveis: {"{{customer_name}}, {{order_id}}, {{total}}, {{order_type}}, {{ready_message}}, {{restaurant_name}}, {{tracking_url}}"}
                    </p>
                    <div className="mt-3 p-3 rounded-lg bg-muted/50 border border-border space-y-1.5">
                      <p className="text-xs font-semibold text-foreground">📖 Legenda das variáveis:</p>
                      <ul className="text-xs text-muted-foreground space-y-1 list-none">
                        <li><code className="bg-muted px-1 py-0.5 rounded text-[11px]">{"{{customer_name}}"}</code> — Nome do cliente</li>
                        <li><code className="bg-muted px-1 py-0.5 rounded text-[11px]">{"{{order_id}}"}</code> — Código curto do pedido (8 caracteres)</li>
                        <li><code className="bg-muted px-1 py-0.5 rounded text-[11px]">{"{{total}}"}</code> — Valor total do pedido (ex: 45.90)</li>
                        <li><code className="bg-muted px-1 py-0.5 rounded text-[11px]">{"{{order_type}}"}</code> — Tipo do pedido (🛵 Delivery, 🍽️ Mesa, 🏃 Retirada)</li>
                        <li><code className="bg-muted px-1 py-0.5 rounded text-[11px]">{"{{ready_message}}"}</code> — Mensagem automática de retirada/entrega</li>
                        <li><code className="bg-muted px-1 py-0.5 rounded text-[11px]">{"{{restaurant_name}}"}</code> — Nome do restaurante</li>
                        <li><code className="bg-muted px-1 py-0.5 rounded text-[11px]">{"{{tracking_url}}"}</code> — Link de acompanhamento e avaliação do pedido</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button onClick={handleSaveTemplates} className="w-full gap-2">
                <Check className="h-4 w-4" /> Salvar Templates
              </Button>
            </>
          )}
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Mensagens Recentes</CardTitle>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhuma mensagem ainda</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {messages.map(msg => (
                    <div key={msg.id} className={`p-2.5 rounded-lg text-sm ${
                      msg.direction === 'outbound' 
                        ? 'bg-green-50 dark:bg-green-900/20 ml-6' 
                        : 'bg-muted mr-6'
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">
                          {msg.direction === 'outbound' ? '📤 Enviada' : '📥 Recebida'} • +{msg.remote_phone}
                        </span>
                        <div className="flex items-center gap-1">
                          <Badge variant={msg.status === 'sent' || msg.status === 'received' ? 'default' : 'destructive'} className="text-[10px] px-1.5 py-0">
                            {msg.status}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(msg.created_at).toLocaleString('pt-BR')}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Chatbot IA Tab */}
        <TabsContent value="chatbot" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500" /> Chatbot com IA
                </CardTitle>
                <Switch
                  checked={chatbotConfig?.is_enabled ?? false}
                  onCheckedChange={handleToggleChatbot}
                  disabled={!instance || instance.status !== 'connected'}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg border border-purple-300/50 bg-purple-50 dark:bg-purple-950/20 dark:border-purple-700/40">
                <Bot className="h-5 w-5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-purple-800 dark:text-purple-300">Atendimento híbrido com IA</p>
                  <p className="text-xs text-purple-700/80 dark:text-purple-400/70 mt-0.5">
                    O bot responde automaticamente usando o cardápio, horários e promoções do restaurante. 
                    Interpreta textos, imagens e áudios. O lojista pode assumir a conversa a qualquer momento.
                  </p>
                </div>
              </div>

              {(!instance || instance.status !== 'connected') && (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground">Conecte o WhatsApp primeiro para usar o chatbot IA</p>
                </div>
              )}

              {chatbotConfig && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-medium">Mensagem de boas-vindas</Label>
                    <Textarea
                      value={chatbotConfig.greeting_message}
                      onChange={e => setChatbotConfig({ ...chatbotConfig, greeting_message: e.target.value })}
                      rows={2}
                      placeholder="Olá! 👋 Sou o assistente virtual..."
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-medium">Prompt do sistema (personalidade do bot)</Label>
                    <Textarea
                      value={chatbotConfig.system_prompt}
                      onChange={e => setChatbotConfig({ ...chatbotConfig, system_prompt: e.target.value })}
                      rows={4}
                      placeholder="Você é um assistente virtual..."
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      O cardápio, horários, promoções e pedidos são injetados automaticamente no contexto
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs font-medium">Mensagem de fallback</Label>
                    <Textarea
                      value={chatbotConfig.fallback_message}
                      onChange={e => setChatbotConfig({ ...chatbotConfig, fallback_message: e.target.value })}
                      rows={2}
                      placeholder="Desculpe, não consegui entender..."
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-medium">Link do cardápio digital (usado pelo bot)</Label>
                    <Input
                      value={chatbotConfig.menu_url || ''}
                      onChange={e => setChatbotConfig({ ...chatbotConfig, menu_url: e.target.value })}
                      placeholder={restaurant?.slug ? `https://viamenu.com.br/${restaurant.slug}` : 'https://viamenu.com.br/sua-loja'}
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      O bot enviará este link quando clientes pedirem o cardápio. {restaurant?.slug && !chatbotConfig.menu_url && (
                        <button 
                          type="button" 
                          className="text-primary underline" 
                          onClick={() => setChatbotConfig({ ...chatbotConfig, menu_url: `https://viamenu.com.br/${restaurant.slug}` })}
                        >
                          Preencher automaticamente
                        </button>
                      )}
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs font-medium">Máx. mensagens no contexto</Label>
                    <Input
                      type="number"
                      value={chatbotConfig.max_context_messages}
                      onChange={e => setChatbotConfig({ ...chatbotConfig, max_context_messages: parseInt(e.target.value) || 20 })}
                      min={5}
                      max={50}
                    />
                  </div>

                  <Button onClick={handleSaveChatbot} disabled={savingChatbot} className="w-full gap-2">
                    {savingChatbot ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Salvar Configuração
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Chatbot Conversations */}
          {chatbotConversations.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" /> Conversas Ativas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {chatbotConversations.map(conv => (
                    <div key={conv.id} className="flex items-center justify-between p-2.5 rounded-lg border border-border">
                      <div>
                        <p className="text-sm font-medium">+{conv.remote_phone}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {conv.last_message_at ? new Date(conv.last_message_at).toLocaleString('pt-BR') : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={conv.status === 'bot_active' ? 'default' : 'secondary'} className="text-[10px]">
                          {conv.status === 'bot_active' ? (
                            <><Bot className="h-3 w-3 mr-1" /> Bot</>
                          ) : (
                            <><UserRound className="h-3 w-3 mr-1" /> Humano</>
                          )}
                        </Badge>
                        {conv.status === 'human_active' && (
                          <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => handleResetConversation(conv.id)}>
                            Devolver ao bot
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Flows Tab */}
        <TabsContent value="flows" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-blue-500" /> Fluxos de Resposta
                </CardTitle>
                <Button variant="outline" size="sm" onClick={handleAddFlow} className="gap-1">
                  <Plus className="h-3.5 w-3.5" /> Novo Fluxo
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg border border-blue-300/50 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-700/40">
                <GitBranch className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Respostas automáticas por palavras-chave</p>
                  <p className="text-xs text-blue-700/80 dark:text-blue-400/70 mt-0.5">
                    Configure respostas prontas que são acionadas por palavras-chave. Fluxos têm prioridade sobre a IA — 
                    se uma palavra-chave bater, a resposta configurada é enviada diretamente com botões interativos.
                  </p>
                </div>
              </div>

              {chatbotFlows.length === 0 ? (
                <div className="text-center py-8 space-y-3">
                  <p className="text-sm text-muted-foreground">Nenhum fluxo configurado</p>
                  <Button onClick={handleAddFlow} variant="outline" className="gap-2">
                    <Plus className="h-4 w-4" /> Criar Primeiro Fluxo
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {chatbotFlows.map((flow, flowIdx) => (
                    <Card key={flow.id} className="border-border">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                            <Input
                              value={flow.title}
                              onChange={e => handleUpdateFlow(flowIdx, 'title', e.target.value)}
                              placeholder="Nome do fluxo (ex: Enviar Cardápio)"
                              className="text-sm font-medium h-8"
                            />
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <Switch
                              checked={flow.is_active}
                              onCheckedChange={v => handleUpdateFlow(flowIdx, 'is_active', v)}
                            />
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveFlow(flowIdx)} className="h-8 w-8 p-0 text-destructive">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <Label className="text-xs font-medium">Palavras-chave (separadas por vírgula)</Label>
                          <Input
                            value={(flow.trigger_keywords || []).join(', ')}
                            onChange={e => handleUpdateFlow(flowIdx, 'trigger_keywords', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                            placeholder="cardápio, menu, link, ver produtos"
                            className="text-sm"
                          />
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Se a mensagem do cliente contiver qualquer uma dessas palavras, essa resposta será enviada
                          </p>
                        </div>

                        <div>
                          <Label className="text-xs font-medium">Resposta</Label>
                          <Textarea
                            value={flow.response_text}
                            onChange={e => handleUpdateFlow(flowIdx, 'response_text', e.target.value)}
                            placeholder="Olá! 😊 Acesse nosso cardápio digital aqui: https://..."
                            rows={3}
                            className="text-sm"
                          />
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs font-medium">Botões de resposta rápida (máx. 3)</Label>
                            {flow.buttons.length < 3 && (
                              <Button variant="ghost" size="sm" onClick={() => handleAddFlowButton(flowIdx)} className="h-6 text-xs gap-1">
                                <Plus className="h-3 w-3" /> Botão
                              </Button>
                            )}
                          </div>
                          {flow.buttons.map((btn, btnIdx) => (
                            <div key={btnIdx} className="flex items-center gap-2 mb-2">
                              <Input
                                value={btn.label}
                                onChange={e => handleUpdateFlowButton(flowIdx, btnIdx, 'label', e.target.value)}
                                placeholder="Texto do botão (máx. 20 chars)"
                                className="text-xs h-8"
                                maxLength={20}
                              />
                              <Input
                                value={btn.value}
                                onChange={e => handleUpdateFlowButton(flowIdx, btnIdx, 'value', e.target.value)}
                                placeholder="Resposta ao clicar"
                                className="text-xs h-8"
                              />
                              <Button variant="ghost" size="sm" onClick={() => handleRemoveFlowButton(flowIdx, btnIdx)} className="h-8 w-8 p-0 text-destructive shrink-0">
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                          {flow.buttons.length === 0 && (
                            <p className="text-[10px] text-muted-foreground">
                              Botões aparecem como opções clicáveis no WhatsApp do cliente
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <Button onClick={handleSaveFlows} disabled={savingFlows} className="w-full gap-2">
                    {savingFlows ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Salvar Fluxos
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WhatsAppConfig;
